import { LEVEL_OPTIONS } from '../constants/competitionConstants';

/**
 * @typedef {Object} LevelNormalization
 * @property {string} level
 * @property {Object|null} levelOption
 */

/**
 * Build a lookup map from aliases to level options for fast normalization
 */
const buildAliasMap = () => {
  const map = new Map();

  LEVEL_OPTIONS.forEach(option => {
    // Map the value itself
    map.set(option.value.toLowerCase(), option);
    map.set(option.label.toLowerCase(), option);

    // Map all aliases
    if (option.aliases) {
      option.aliases.forEach(alias => {
        map.set(alias.toLowerCase(), option);
      });
    }
  });

  return map;
};

// Lazily build the alias map
let aliasMap = null;
const getAliasMap = () => {
  if (!aliasMap) {
    aliasMap = buildAliasMap();
  }
  return aliasMap;
};

/**
 * Normalize a level string to its canonical form using LEVEL_OPTIONS
 *
 * @param {string} levelCode - The LevelCode from PTF (e.g., "MLEVEL06JE", "M3D1")
 * @param {string} levelName - The Level name from PTF (e.g., "6", "3")
 * @returns {LevelNormalization} - { level: canonical level string, levelOption: full LEVEL_OPTIONS entry or null }
 *
 * Examples:
 *   normalizeLevel("MLEVEL06JE", "6") => { level: "6 JE", levelOption: {...} }
 *   normalizeLevel("M3D1", "3") => { level: "3 D1", levelOption: {...} }
 *   normalizeLevel("", "Level 5") => { level: "Level 5", levelOption: {...} }
 */
export const normalizeLevel = (levelCode, levelName) => {
  const map = getAliasMap();

  // Try LevelCode first (most specific)
  if (levelCode) {
    const codeMatch = map.get(levelCode.toLowerCase());
    if (codeMatch) {
      return { level: codeMatch.label, levelOption: codeMatch };
    }
  }

  // Try the level name
  if (levelName) {
    const nameMatch = map.get(levelName.toLowerCase());
    if (nameMatch) {
      return { level: nameMatch.label, levelOption: nameMatch };
    }

    // Try with "Level " prefix
    const withPrefix = map.get(`level ${levelName}`.toLowerCase());
    if (withPrefix) {
      return { level: withPrefix.label, levelOption: withPrefix };
    }
  }

  // No match found - return the original level name
  return { level: levelName || levelCode || '', levelOption: null };
};

/**
 * Find a level option by any identifier (value, label, or alias)
 *
 * @param {string} identifier - Any level identifier
 * @returns {Object|null} - The matching LEVEL_OPTIONS entry or null
 */
export const findLevelOption = (identifier) => {
  if (!identifier) return null;
  return getAliasMap().get(identifier.toLowerCase()) || null;
};

/**
 * Check if two level identifiers refer to the same level
 *
 * @param {string} level1 - First level identifier
 * @param {string} level2 - Second level identifier
 * @returns {boolean} - True if they refer to the same canonical level
 */
export const isSameLevel = (level1, level2) => {
  if (!level1 || !level2) return false;
  if (level1.toLowerCase() === level2.toLowerCase()) return true;

  const option1 = findLevelOption(level1);
  const option2 = findLevelOption(level2);

  if (!option1 || !option2) return false;
  return option1.value === option2.value;
};

/**
 * Get all levels for a specific gender
 *
 * @param {string} gender - 'M' for Men, 'F' for Women, 'R' for Rhythmic
 * @returns {Array} - Array of LEVEL_OPTIONS entries for that gender
 */
export const getLevelsByGender = (gender) => {
  return LEVEL_OPTIONS.filter(option => option.gender === gender);
};

export default normalizeLevel;
